// The selection algebra of the multi-select variant of OptionSelectBase: what a
// click on one option does to the whole selection array.
//
// Co-located with its only consumer and deliberately import-free at value level:
// `yarn test:unit` runs the sibling test under bare `node --test`, which cannot
// resolve `@render/*` at runtime and refuses `enum` — so `OnSelectBehavior` must
// not be imported here. The behaviour is taken as a plain string union instead;
// the caller passes the enum member, and a string enum is assignable to its own
// literal type, so renaming a member's value breaks the call site rather than
// silently disabling a branch here.
import type { OptionSelectOption } from '@api/types.gen'

/** The literal spellings of `OnSelectBehavior` — pinned by the call site. */
export type SelectBehavior = 'none' | 'select_all' | 'deselect_all'

export interface MultiSelectionInput {
  /** Every option in the list, in author order. */
  options: readonly OptionSelectOption[]
  /** The current selection — the option values already picked. */
  selection: readonly string[]
  /** The option that was clicked. */
  clicked: OptionSelectOption
  /** `clicked.onSelectBehavior`, resolved to its default. */
  behavior: SelectBehavior
  /** The `maxSelections` cap; unset or non-positive means unlimited. */
  maxSelections?: number
}

/**
 * The option values a bulk action on `clicked` must leave alone.
 *
 * Exceptions are stored as option **ids**, so renaming an option's value cannot
 * silently unpick it from a list. An option can never except itself: the click
 * always applies to the option clicked.
 */
function exceptedValuesOf(
  clicked: OptionSelectOption,
  options: readonly OptionSelectOption[],
): Set<string> {
  const ids = clicked.onSelectBehaviorExceptions ?? []
  return new Set(
    options.filter((o) => o.id !== clicked.id && ids.includes(o.id)).map((o) => o.value),
  )
}

/**
 * The next selection after clicking `clicked`, or `null` when the click is
 * blocked and nothing at all should happen (not even the author's `onClick`).
 *
 * An excepted option is never driven either way — whatever state it is in is
 * carried through — which is what makes "deselect all others *except* X" and
 * "select all others *except* Y" mean the same thing from both sides.
 *
 * A "select all others" option stays selected only while every option it
 * reaches is selected: a click that unpicks one of them unpicks it too. The
 * converse is not automatic — picking the last missing option by hand does not
 * re-arm it, since "everything picked, the option itself off" is the state its
 * own second click produces.
 */
export function nextMultiSelection({
  options,
  selection,
  clicked,
  behavior,
  maxSelections,
}: MultiSelectionInput): string[] | null {
  const allValues = options.map((o) => o.value)
  const exceptedValues = exceptedValuesOf(clicked, options)
  const keptExceptions = selection.filter((v) => exceptedValues.has(v))

  if (behavior === 'select_all') {
    const targetValues = allValues.filter((v) => !exceptedValues.has(v))
    // Already everything it reaches? Then the click toggles the option itself
    // back off and leaves the rest of the list standing.
    return targetValues.every((v) => selection.includes(v))
      ? selection.filter((v) => v !== clicked.value)
      : allValues.filter((v) => !exceptedValues.has(v) || keptExceptions.includes(v))
  }

  if (behavior === 'deselect_all') {
    // "Only me" means "me, plus whichever exceptions were already selected" —
    // precisely the state a previous click on this option leaves behind.
    const isOnlySelf =
      selection.includes(clicked.value) &&
      selection.every((v) => v === clicked.value || exceptedValues.has(v))
    return isOnlySelf
      ? selection.filter((v) => v !== clicked.value)
      : allValues.filter((v) => v === clicked.value || keptExceptions.includes(v))
  }

  const toggled = selection.includes(clicked.value)
    ? selection.filter((v) => v !== clicked.value)
    : [...selection, clicked.value]

  // A "deselect all others" option is mutually exclusive with the regular
  // options, so picking one must clear it — unless it excepts the option just
  // picked, which is exactly the pairing an exception exists to allow.
  const conflicting = new Set(
    options
      .filter(
        (o) =>
          o.onSelectBehavior === 'deselect_all' &&
          !(o.onSelectBehaviorExceptions ?? []).includes(clicked.id),
      )
      .map((o) => o.value),
  )
  const kept = toggled.filter((v) => !conflicting.has(v))

  const staleSelectAll = new Set(
    options
      .filter((o) => {
        if (o.onSelectBehavior !== 'select_all') return false
        const excepted = exceptedValuesOf(o, options)
        return options.some((t) => !excepted.has(t.value) && !kept.includes(t.value))
      })
      .map((o) => o.value),
  )
  const next = kept.filter((v) => !staleSelectAll.has(v))

  // Block only a click that GROWS the selection past the limit. Deselecting and
  // deselect-all swaps (which keep/shrink the count) are always allowed, so the
  // user can never get stuck even if saved data starts over the limit.
  const wouldGrow = next.length > selection.length
  const wouldExceed = maxSelections != null && maxSelections > 0 && next.length > maxSelections

  return wouldGrow && wouldExceed ? null : next
}
